/**
 * Assets API Types - Zod Schemas & DTOs
 * Anti-corruption layer: validates and types API responses
 */

import { z } from '@spcc/core'

// ============================================
// Shared Schemas
// ============================================

export const capacitySchema = z.object({
  value: z.number().positive(),
  unit: z.enum(['tons', 'm³', 'kg']),
})

// ============================================
// Driver Schemas
// ============================================

export const driverStatusBackendSchema = z.enum(['ACTIVE', 'INACTIVE', 'SUSPENDED'])
export const licenseTypeSchema = z.string()
export const driverPreferredLanguageSchema = z.enum(['en', 'ar', 'ur'])

export const driverDtoSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  name: z.string(),
  driverId: z.string().optional(),
  licenseNumber: z.string(),
  licenseType: licenseTypeSchema,
  licenseExpiry: z.string(),
  nationality: z.string().optional(),
  phone: z.string().optional(),
  whatsappNumber: z.string().optional(),
  preferredLanguage: driverPreferredLanguageSchema.optional(),
  status: driverStatusBackendSchema,
  metadata: z.record(z.string(), z.unknown()).optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable().optional(),
})

export const createDriverRequestSchema = z.object({
  tenantId: z.string().min(1, 'Tenant ID is required'),
  name: z.string().min(1, 'Name is required'),
  driverId: z.string().optional(),
  licenseNumber: z.string().min(1, 'License number is required'),
  licenseType: z.string().min(1, 'License type is required'),
  licenseExpiry: z.string().min(1, 'License expiry is required'),
  nationality: z.string().optional(),
  phone: z.string().optional(),
  whatsappNumber: z.string().optional(),
  preferredLanguage: driverPreferredLanguageSchema.optional(),
  status: driverStatusBackendSchema.optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
})

export const updateDriverRequestSchema = z.object({
  name: z.string().optional(),
  driverId: z.string().optional(),
  licenseNumber: z.string().optional(),
  licenseType: z.string().optional(),
  licenseExpiry: z.string().optional(),
  nationality: z.string().optional(),
  phone: z.string().optional(),
  whatsappNumber: z.string().optional(),
  preferredLanguage: driverPreferredLanguageSchema.optional(),
  status: driverStatusBackendSchema.optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
})

export const driversFilterSchema = z.object({
  status: z.union([driverStatusBackendSchema, z.literal('all')]).optional(),
  licenseType: z.string().optional(),
  search: z.string().optional(),
})

// ============================================
// Vehicle/Asset Schemas
// ============================================

export const vehicleStatusBackendSchema = z.enum([
  'active',
  'inactive',
  'maintenance',
  'retired',
  'disposed',
])

export const ownershipTypeBackendSchema = z.enum([
  'OWNED',
  'LEASED_IN',
  'RENTED_OUT',
  'OUTSOURCED',
  'BROKERED',
  'SHARED',
])

export const assetDtoSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  category: z.string(),
  plateNumber: z.string().optional(),
  vin: z.string().optional(),
  serialNumber: z.string().optional(),
  year: z.number().optional(),
  ownershipType: ownershipTypeBackendSchema,
  status: vehicleStatusBackendSchema,
  metadata: z.record(z.string(), z.unknown()).optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export const createAssetRequestSchema = z.object({
  tenantId: z.string().min(1, 'Tenant ID is required'),
  category: z.string().min(1, 'Category is required'),
  ownershipType: ownershipTypeBackendSchema,
  year: z.number().optional(),
  plateNumber: z.string().optional(),
  vin: z.string().optional(),
  serialNumber: z.string().optional(),
  status: vehicleStatusBackendSchema.optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
})

export const updateAssetRequestSchema = z.object({
  category: z.string().optional(),
  ownershipType: ownershipTypeBackendSchema.optional(),
  year: z.number().optional(),
  plateNumber: z.string().optional(),
  vin: z.string().optional(),
  serialNumber: z.string().optional(),
  status: vehicleStatusBackendSchema.optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
})

export const vehiclesFilterSchema = z.object({
  status: z.union([vehicleStatusBackendSchema, z.literal('all')]).optional(),
  ownershipType: z.union([ownershipTypeBackendSchema, z.literal('all')]).optional(),
  category: z.string().optional(),
  search: z.string().optional(),
})

// ============================================
// Trailer Schemas (reuses Asset schema)
// ============================================

export const trailersFilterSchema = z.object({
  status: z.union([vehicleStatusBackendSchema, z.literal('all')]).optional(),
  search: z.string().optional(),
})

// ============================================
// Resource Schemas
// ============================================

export const resourceStatusBackendSchema = z.enum(['ACTIVE', 'INACTIVE'])

export const resourceDtoSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  name: z.string(),
  description: z.string().optional(),
  powerUnitId: z.string().optional(),
  trailerId: z.string().optional(),
  driverId: z.string().optional(),
  status: resourceStatusBackendSchema,
  metadata: z.record(z.string(), z.unknown()).optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable().optional(),
})

export const createResourceRequestSchema = z.object({
  tenantId: z.string().min(1, 'Tenant ID is required'),
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  powerUnitId: z.string().optional(),
  trailerId: z.string().optional(),
  driverId: z.string().optional(),
  status: resourceStatusBackendSchema.optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
})

export const updateResourceRequestSchema = z.object({
  name: z.string().optional(),
  description: z.string().optional(),
  powerUnitId: z.string().optional(),
  trailerId: z.string().optional(),
  driverId: z.string().optional(),
  status: resourceStatusBackendSchema.optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
})

export const resourcesFilterSchema = z.object({
  status: z.union([resourceStatusBackendSchema, z.literal('all')]).optional(),
  search: z.string().optional(),
})

// ============================================
// Stats Schemas
// ============================================

export const fleetStatsDtoSchema = z.object({
  totalDrivers: z.number(),
  availableDrivers: z.number(),
  totalVehicles: z.number(),
  availableVehicles: z.number(),
  totalResources: z.number(),
  activeResources: z.number(),
  onDelivery: z.number(),
})

// ============================================
// Response Schemas
// ============================================

export const driversListResponseSchema = z.object({
  data: z.array(driverDtoSchema),
  meta: z.object({
    total: z.number(),
    page: z.number(),
    pageSize: z.number(),
  }).optional(),
})

export const driverResponseSchema = z.object({
  data: driverDtoSchema,
})

export const assetsListResponseSchema = z.object({
  data: z.array(assetDtoSchema),
  meta: z.object({
    total: z.number(),
    page: z.number(),
    pageSize: z.number(),
  }).optional(),
})

export const assetResponseSchema = z.object({
  data: assetDtoSchema,
})

export const resourcesListResponseSchema = z.object({
  data: z.array(resourceDtoSchema),
  meta: z.object({
    total: z.number(),
    page: z.number(),
    pageSize: z.number(),
  }).optional(),
})

export const resourceResponseSchema = z.object({
  data: resourceDtoSchema,
})

// ============================================
// Inferred Types
// ============================================

// Driver Types
export type DriverDto = z.infer<typeof driverDtoSchema>
export type CreateDriverRequestDto = z.infer<typeof createDriverRequestSchema>
export type UpdateDriverRequestDto = z.infer<typeof updateDriverRequestSchema>
export type DriversFilterDto = z.infer<typeof driversFilterSchema>
export type DriversListResponse = z.infer<typeof driversListResponseSchema>
export type DriverResponse = z.infer<typeof driverResponseSchema>

// Asset/Vehicle Types
export type AssetDto = z.infer<typeof assetDtoSchema>
export type CreateAssetRequestDto = z.infer<typeof createAssetRequestSchema>
export type UpdateAssetRequestDto = z.infer<typeof updateAssetRequestSchema>
export type VehiclesFilterDto = z.infer<typeof vehiclesFilterSchema>
export type AssetsListResponse = z.infer<typeof assetsListResponseSchema>
export type AssetResponse = z.infer<typeof assetResponseSchema>

// Trailer Types (reuses Asset types)
export type TrailerDto = AssetDto
export type CreateTrailerRequestDto = CreateAssetRequestDto
export type UpdateTrailerRequestDto = UpdateAssetRequestDto
export type TrailersFilterDto = z.infer<typeof trailersFilterSchema>

// Resource Types
export type ResourceDto = z.infer<typeof resourceDtoSchema>
export type CreateResourceRequestDto = z.infer<typeof createResourceRequestSchema>
export type UpdateResourceRequestDto = z.infer<typeof updateResourceRequestSchema>
export type ResourcesFilterDto = z.infer<typeof resourcesFilterSchema>
export type ResourcesListResponse = z.infer<typeof resourcesListResponseSchema>
export type ResourceResponse = z.infer<typeof resourceResponseSchema>

// Stats Types
export type FleetStatsDto = z.infer<typeof fleetStatsDtoSchema>

// ============================================
// Validation Helpers
// ============================================

// Driver Validation
export function validateDriverDto(data: unknown): DriverDto {
  return driverDtoSchema.parse(data)
}

export function validateDriversResponse(data: unknown): DriverDto[] {
  if (Array.isArray(data)) {
    return z.array(driverDtoSchema).parse(data)
  }
  const response = driversListResponseSchema.parse(data)
  return response.data
}

// Asset Validation
export function validateAssetDto(data: unknown): AssetDto {
  return assetDtoSchema.parse(data)
}

export function validateAssetsResponse(data: unknown): AssetDto[] {
  if (Array.isArray(data)) {
    return z.array(assetDtoSchema).parse(data)
  }
  const response = assetsListResponseSchema.parse(data)
  return response.data
}

// Resource Validation
export function validateResourceDto(data: unknown): ResourceDto {
  return resourceDtoSchema.parse(data)
}

export function validateResourcesResponse(data: unknown): ResourceDto[] {
  if (Array.isArray(data)) {
    return z.array(resourceDtoSchema).parse(data)
  }
  const response = resourcesListResponseSchema.parse(data)
  return response.data
}

// Stats Validation
export function validateFleetStats(data: unknown): FleetStatsDto {
  return fleetStatsDtoSchema.parse(data)
}

// ============================================
// API Endpoints
// ============================================

export const DRIVER_ENDPOINTS = {
  LIST: '/api/v1/drivers',
  DETAIL: (id: string) => `/api/v1/drivers/${id}`,
  CREATE: '/api/v1/drivers',
  UPDATE: (id: string) => `/api/v1/drivers/${id}`,
  DELETE: (id: string) => `/api/v1/drivers/${id}`,
} as const

export const ASSET_ENDPOINTS = {
  LIST: '/api/v1/assets',
  DETAIL: (id: string) => `/api/v1/assets/${id}`,
  CREATE: '/api/v1/assets',
  UPDATE: (id: string) => `/api/v1/assets/${id}`,
  DELETE: (id: string) => `/api/v1/assets/${id}`,
  BY_CATEGORY: (category: string) => `/api/v1/assets?category=${category}`,
} as const

// Strapi endpoints (for direct Strapi integration)
export const STRAPI_ASSET_ENDPOINTS = {
  LIST: '/api/assets',
  DETAIL: (documentId: string) => `/api/assets/${documentId}`,
  CREATE: '/api/assets',
  UPDATE: (documentId: string) => `/api/assets/${documentId}`,
  FLEET_STATS: '/api/assets/fleet-stats',
  ASSET_TYPES: '/api/asset-types',
  BULK_IMPORT: '/api/assets/bulk-import',
} as const

export const STRAPI_DRIVER_ENDPOINTS = {
  LIST: '/api/drivers',
  DETAIL: (documentId: string) => `/api/drivers/${documentId}`,
  CREATE: '/api/drivers',
  UPDATE: (documentId: string) => `/api/drivers/${documentId}`,
  DELETE: (documentId: string) => `/api/drivers/${documentId}`,
  BULK_IMPORT: '/api/drivers/bulk-import',
} as const

export const STRAPI_RESOURCE_ENDPOINTS = {
  LIST: '/api/resources',
  DETAIL: (documentId: string) => `/api/resources/${documentId}`,
  CREATE: '/api/resources',
  UPDATE: (documentId: string) => `/api/resources/${documentId}`,
  DELETE: (documentId: string) => `/api/resources/${documentId}`,
  BULK_IMPORT: '/api/resources/bulk-import',
} as const

// Strapi Asset DTO Schema (from Strapi content-types)
export const strapiAssetDtoSchema = z.object({
  id: z.number(),
  documentId: z.string(),
  assetNumber: z.string(),
  // type is now a required relation to Asset Type
  type: z.union([
    z.object({
      id: z.number(),
      documentId: z.string(),
      code: z.string(),
      name: z.string(),
    }),
    z.null(),
  ]),
  // Legacy: assetType kept for backward compatibility (may be removed)
  assetType: z.union([
    z.object({
      id: z.number(),
      documentId: z.string(),
      code: z.string(),
      name: z.string(),
    }),
    z.null(),
  ]).optional(),
  make: z.string(),
  model: z.string(),
  year: z.number().nullable().optional(),
  vin: z.string().nullable().optional(),
  licensePlate: z.string().nullable().optional(),
  assetStatus: z.string().default('active'),
  purchasePrice: z.number().nullable().optional(),
  purchaseDate: z.string().nullable().optional(),
  currentMileage: z.number().nullable().optional(),
  capacity: z.number().nullable().optional(),
  capacityUnit: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  tenant: z.union([
    z.object({
      id: z.number(),
      documentId: z.string(),
      name: z.string(),
    }),
    z.null(),
  ]).optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export const strapiAssetListResponseSchema = z.object({
  data: z.array(strapiAssetDtoSchema),
  meta: z.object({
    pagination: z.object({
      page: z.number(),
      pageSize: z.number(),
      pageCount: z.number(),
      total: z.number(),
    }).optional(),
  }).optional(),
})

export const strapiAssetResponseSchema = z.object({
  data: strapiAssetDtoSchema,
})

// Strapi Driver DTO Schema (from Strapi content-types)
export const strapiDriverDtoSchema = z.object({
  id: z.number(),
  documentId: z.string(),
  name: z.string(),
  driverId: z.string().nullable().optional(),
  licenseNumber: z.string().nullable().optional(),
  licenseType: z.string().nullable().optional(),
  licenseExpiry: z.string().nullable().optional(),
  nationality: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  whatsappNumber: z.string().nullable().optional(),
  preferredLanguage: driverPreferredLanguageSchema.nullable().optional(),
  driverStatus: driverStatusBackendSchema,
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  deletedAt: z.string().nullable().optional(),
  tenant: z.union([
    z.object({
      id: z.number(),
      documentId: z.string(),
      name: z.string(),
    }),
    z.null(),
  ]).optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export const strapiDriverListResponseSchema = z.object({
  data: z.array(strapiDriverDtoSchema),
  meta: z.object({
    pagination: z.object({
      page: z.number(),
      pageSize: z.number(),
      pageCount: z.number(),
      total: z.number(),
    }).optional(),
  }).optional(),
})

export const strapiDriverResponseSchema = z.object({
  data: strapiDriverDtoSchema,
})

// Strapi Resource DTO Schema (from Strapi content-types)
// Note: relations are kept permissive because Strapi can return them in multiple shapes depending on populate options.
export const strapiResourceDtoSchema = z.object({
  id: z.number(),
  documentId: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  resourceStatus: resourceStatusBackendSchema,
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  deletedAt: z.string().nullable().optional(),
  tenant: z
    .union([
      z.object({
        id: z.number(),
        documentId: z.string(),
        name: z.string(),
      }),
      z.null(),
    ])
    .optional(),
  driver: z.any().nullable().optional(),
  powerUnit: z.any().nullable().optional(),
  trailer: z.any().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export const strapiResourceListResponseSchema = z.object({
  data: z.array(strapiResourceDtoSchema),
  meta: z
    .object({
      pagination: z
        .object({
          page: z.number(),
          pageSize: z.number(),
          pageCount: z.number(),
          total: z.number(),
        })
        .optional(),
    })
    .optional(),
})

export const strapiResourceResponseSchema = z.object({
  data: strapiResourceDtoSchema,
})

export const strapiAssetTypeDtoSchema = z.object({
  id: z.number(),
  documentId: z.string(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  isActive: z.boolean().default(true),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export const strapiAssetTypeListResponseSchema = z.object({
  data: z.array(strapiAssetTypeDtoSchema),
  meta: z.object({
    pagination: z.object({
      page: z.number(),
      pageSize: z.number(),
      pageCount: z.number(),
      total: z.number(),
    }).optional(),
  }).optional(),
})

export type StrapiAssetDto = z.infer<typeof strapiAssetDtoSchema>
export type StrapiAssetListResponse = z.infer<typeof strapiAssetListResponseSchema>
export type StrapiAssetResponse = z.infer<typeof strapiAssetResponseSchema>
export type StrapiDriverDto = z.infer<typeof strapiDriverDtoSchema>
export type StrapiDriverListResponse = z.infer<typeof strapiDriverListResponseSchema>
export type StrapiDriverResponse = z.infer<typeof strapiDriverResponseSchema>
export type StrapiAssetTypeDto = z.infer<typeof strapiAssetTypeDtoSchema>
export type StrapiAssetTypeListResponse = z.infer<typeof strapiAssetTypeListResponseSchema>
export type StrapiResourceDto = z.infer<typeof strapiResourceDtoSchema>
export type StrapiResourceListResponse = z.infer<typeof strapiResourceListResponseSchema>
export type StrapiResourceResponse = z.infer<typeof strapiResourceResponseSchema>

export const RESOURCE_ENDPOINTS = {
  LIST: '/api/v1/resources',
  DETAIL: (id: string) => `/api/v1/resources/${id}`,
  CREATE: '/api/v1/resources',
  UPDATE: (id: string) => `/api/v1/resources/${id}`,
  DELETE: (id: string) => `/api/v1/resources/${id}`,
} as const

export const FLEET_ENDPOINTS = {
  STATS: '/api/v1/fleet/stats',
} as const

// ============================================
// Deactivation Result
// Returned by Strapi update endpoints when a
// Resource / Driver / Asset is deactivated.
// ============================================

export interface DeactivationResult {
  /** orderNumbers of orders whose shipments were auto-unassigned (pending/hold only) */
  affectedOrders: string[]
  /** resource names that were auto-set to INACTIVE (driver/asset deactivation only) */
  affectedResources?: string[]
}

/** Wraps every entity update response with optional deactivation side-effects */
export interface EntityUpdateResult<T> {
  entity: T
  deactivation: DeactivationResult
}
